import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { useId, useMemo, useState, type ReactNode } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { Barra, Carregando, ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { formatarDataHora, formatarDia, formatarInteiro, formatarReais } from '@/lib/formato';
import { retornoDe } from '@/lib/retorno';
import { combinaComTermo } from '@/lib/texto';
import { useSessao } from '@/modulos/sessao/consultas';

import { EscolhaDePermissoes } from './EscolhaDePermissoes';
import { acoesSemLeitura } from './leitura-do-modulo';
import { MODULOS_DO_V6, rotuloDoModulo } from './modulos-do-v6';

import { enviarJson, ErroDaApi, pedir } from './api';
import { EtiquetaDeParceiro, MensagemDeErro, NotaDeEscopo, Situacao, SituacaoDePessoa } from './Avisos';
import { FornecedorDoPapel, JanelaDeDuplicar } from './DuplicarPapel';
import { fornecedorDaConcessao, fornecedorEscrito, permissoesOferecidas, TRAVA_DO_PARCEIRO } from './papel-de-parceiro';
import { DesenhoDaConcessao } from './Concessao';
import { InicioDoPapel } from './InicioDoPapel';
import { TravaDaPessoa, TravaDoLancamento } from './TravaDoLancamento';
import { ROTULO_DE_SUPORTE } from '@/modulos/sessao/conta-de-suporte';
import { enderecoDoConvite, fraseDaEntrega, situacaoDoAcesso, situacaoDoEnvio } from './convite';
import {
  NOME_DA_VISTA,
  OPCOES_POR_PAGINA,
  PADRAO_POR_PAGINA,
  VISTAS,
  VISTA_PADRAO,
  validarBuscaDeGovernanca,
  type Vista,
} from './busca';
import {
  useAlcadas,
  useCriarPermissao,
  useCriarPessoa,
  useEmitirConvite,
  useEntregaDoConvite,
  useGravarAlcada,
  useGravarPessoa,
  useGravarPapel,
  usePapeis,
  usePermissoes,
  usePermissoesDoPapel,
  usePessoas,
  useRemoverAlcada,
  useCadastro,
  useReferencias,
  useVincularPapel,
  useVincularPermissao,
} from './consultas';
import {
  ASSUNTOS,
  NOME_DO_ASSUNTO,
  type Alcada,
  type Assunto,
  type Papel,
  type ConviteEmitido,
  type Pessoa,
  type PapelDaPessoa,
  type TipoDeAlcance,
  alcanceEscrito,
  O_QUE_O_ALCANCE_VE,
  rotuloDe,
} from './tipos';

const rota = getRouteApi('/cadastros/governanca');

/**
 * Modulo 01, ferramenta 2 — "define permissoes por funcao e responsaveis por
 * compras, ajustes, OS e liberacoes".
 *
 * ## A tela diz, em cima, o que ela ainda NAO faz
 *
 * Papel, permissao e alcada gravam de verdade, com trilha. O papel ainda nao
 * vale so numa filial ou obra, e a alcada ainda nao trava aprovacao nenhuma.
 * Quem abre esta tela precisa saber disso ANTES de concluir que o acesso esta
 * controlado — uma tela de permissoes que nao avisa e pior do que nenhuma, e
 * essa e a razao da nota de escopo, que e diferente do aviso de demonstracao:
 * os dados aqui sao reais.
 *
 * ## A matriz continua vazia de proposito
 *
 * O guia de construcao lista a matriz de permissoes tres vezes como pendencia,
 * e no Apendice A a caixa esta desmarcada. O que o sistema faz e dar o lugar
 * para desenha-la; o desenho e do dono. Nenhuma permissao vem semeada, porque
 * semear seria decidir no lugar dele e depois chamar isso de requisito.
 *
 * ## O desenho (R01, p.28 e p.29)
 *
 * O painel de detalhe e IRMAO do conteudo que rola, como em /cadastros: fixo ao
 * lado da lista a partir de 1280 px, gaveta abaixo. Ele nao pode morar dentro
 * da `pagina`, senao cai no fluxo, abaixo da tabela, com a altura das 49
 * permissoes. Cadastrar papel, pessoa e alcada sao tarefas curtas (2 a 4
 * campos): abrem em janela, e o laranja que as abre fica no cabecalho.
 */
export function PaginaGovernanca() {
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();

  const vista = busca.vista ?? VISTA_PADRAO;
  const termo = busca.termo ?? '';
  const pagina = busca.pagina ?? 1;
  const porPagina = busca.porPagina ?? PADRAO_POR_PAGINA;

  /** A linha aberta no painel: papel ou pessoa, conforme a vista. */
  const [aberto, setAberto] = useState<string | null>(null);
  const [cadastrando, setCadastrando] = useState(false);
  /** 0132 · o papel que está sendo duplicado: a janela abre, o painel sai. */
  const [duplicando, setDuplicando] = useState<Papel | null>(null);

  /* Os pedidos de gravar moram aqui, e nao no formulario: o botao de gravar
     fica no RODAPE FIXO da janela (R01, p.29), fora do corpo que rola, e
     precisa saber se o pedido esta em curso. Ele envia o formulario do corpo
     pelo atributo `form`. */
  const idDoFormulario = useId();
  const gravarPapel = useGravarPapel();
  const criarPessoa = useCriarPessoa();
  const gravarAlcada = useGravarAlcada();
  /* Cadastrar papel, pessoa e alçada só para quem gere a governança (prova do
     marco, 07/10/2026): a Diretoria lê a governança e não a muda. */
  const sessao = useSessao();
  const gere = (sessao.data?.permissoes ?? []).includes('governanca.gerir');
  const gravando: Record<Vista, boolean> = {
    papeis: gravarPapel.isPending,
    pessoas: criarPessoa.isPending,
    alcadas: gravarAlcada.isPending,
  };

  function irPara(estado: EstadoDaBusca) {
    if (estado.vista !== undefined && estado.vista !== vista) setAberto(null);
    void navegar({
      search: validarBuscaDeGovernanca({
        vista: estado.vista ?? vista,
        termo: estado.termo ?? termo,
        pagina: estado.pagina ?? 1,
        porPagina: estado.porPagina ?? porPagina,
      }),
      ...(estado.substituir ? { replace: true } : {}),
    });
  }

  const lista: PropsDaLista = {
    termo,
    pagina,
    porPagina,
    aberto,
    aoAbrir: setAberto,
    ...(gere
      ? {
          aoCadastrar: () => {
            setCadastrando(true);
          },
        }
      : {}),
    irPara,
  };

  function fecharJanela() {
    setCadastrando(false);
    /* A recusa de uma tentativa anterior nao reaparece na proxima abertura. */
    gravarPapel.reset();
    criarPessoa.reset();
    gravarAlcada.reset();
  }

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Cadastros e governança"
            titulo="Perfis e responsáveis"
            subtitulo="Quem faz o quê, e quem aprova até quanto"
            acoes={
              gere ? (
                <Botao
                  nivel="primario"
                  onClick={() => {
                    setCadastrando(true);
                  }}
                >
                  {CADASTRAR[vista]}
                </Botao>
              ) : undefined
            }
          />

          <NotaDeEscopo
            titulo="O que esta tela ainda não faz"
            detalhe="Papéis, permissões e alçadas são gravados com autor, data e motivo, e a permissão já vale: sem ela, o sistema recusa a ação e diz qual pedir. O papel pode valer só numa filial, numa obra ou num contrato (0081, 0083, 0098); quem não vê a empresa inteira não recebe margem. A alçada de compra e a de ajuste de tanque já travam a aprovação; as de ordem de serviço e de liberação ficam gravadas e ainda não travam."
          />

          <Seletor
            rotulo="Vista"
            valor={vista}
            opcoes={VISTAS.map((chave) => ({ valor: chave, rotulo: NOME_DA_VISTA[chave] }))}
            aoEscolher={(escolhida) => {
              irPara({ vista: escolhida });
            }}
          />

          {vista === 'papeis' ? <VistaDePapeis {...lista} /> : null}
          {vista === 'pessoas' ? <VistaDePessoas {...lista} /> : null}
          {vista === 'alcadas' ? <VistaDeAlcadas {...lista} /> : null}
        </div>
      </div>

      {/* Com a janela de cadastro aberta, o painel sai: gaveta e janela
          empilhadas a R01 proibe. A linha continua marcada. */}
      {aberto !== null && !cadastrando && duplicando === null && vista === 'papeis' ? (
        <PainelDoPapel
          id={aberto}
          aoFechar={() => {
            setAberto(null);
          }}
          {...(gere ? { aoDuplicar: setDuplicando } : {})}
        />
      ) : null}
      {duplicando !== null ? (
        <JanelaDeDuplicar
          key={duplicando.id}
          papel={duplicando}
          aberta
          aoFechar={() => {
            setDuplicando(null);
          }}
          aoDuplicado={(novo) => {
            setDuplicando(null);
            setAberto(novo.id);
          }}
        />
      ) : null}
      {aberto !== null && !cadastrando && vista === 'pessoas' ? (
        <PainelDaPessoa
          id={aberto}
          aoFechar={() => {
            setAberto(null);
          }}
        />
      ) : null}

      <Janela
        titulo={CADASTRAR[vista]}
        aberta={cadastrando && gere}
        aoFechar={fecharJanela}
        /* Rodape fixo (p.29): Cancelar a esquerda, o primario a direita. */
        acoes={
          <>
            <Botao nivel="secundario" onClick={fecharJanela}>
              Cancelar
            </Botao>
            <Botao nivel="primario" type="submit" form={idDoFormulario} disabled={gravando[vista]}>
              {CADASTRAR[vista]}
            </Botao>
          </>
        }
      >
        {gere && vista === 'papeis' ? <CadastroDePapel id={idDoFormulario} gravar={gravarPapel} aoConcluir={fecharJanela} /> : null}
        {gere && vista === 'pessoas' ? (
          <CadastroDePessoa id={idDoFormulario} criar={criarPessoa} aoConcluir={fecharJanela} />
        ) : null}
        {gere && vista === 'alcadas' ? (
          <CadastroDeAlcada id={idDoFormulario} gravar={gravarAlcada} aoConcluir={fecharJanela} />
        ) : null}
      </Janela>
    </>
  );
}

/** O primario de cada vista: verbo + objeto (p.15). */
const CADASTRAR: Record<Vista, string> = {
  papeis: 'Cadastrar papel',
  pessoas: 'Cadastrar pessoa',
  alcadas: 'Cadastrar alçada',
};

interface EstadoDaBusca {
  readonly vista?: Vista;
  readonly termo?: string;
  readonly pagina?: number;
  readonly porPagina?: number;
  readonly substituir?: boolean;
}

interface PropsDaLista {
  readonly termo: string;
  readonly pagina: number;
  readonly porPagina: number;
  readonly aberto: string | null;
  readonly aoAbrir: (id: string) => void;
  /** So chega para quem gere a governanca: sem ela, o estado vazio nao oferece cadastrar. */
  readonly aoCadastrar?: () => void;
  readonly irPara: (estado: EstadoDaBusca) => void;
}

// ---------------------------------------------------------- peças da lista

/**
 * A barra de filtros de cada vista, com a CONTAGEM da lista (p.16): "9 de 9 —
 * papéis". Ela mora na vista, e nao na pagina, porque e a vista que sabe
 * quantas linhas tem. Enquanto a lista nao chegou, a contagem vem nula (uma barra): "0 de
 * 0" seria um numero falso (p.32).
 */
function BarraDeBusca({
  termo,
  contagem,
  placeholder,
  irPara,
}: {
  readonly termo: string;
  readonly contagem: string | null;
  readonly placeholder: string;
  readonly irPara: (estado: EstadoDaBusca) => void;
}) {
  const chips: readonly ChipFiltro[] = termo
    ? [
        {
          id: 'termo',
          rotulo: `Busca: ${termo}`,
          aoRemover: () => {
            irPara({ termo: '' });
          },
        },
      ]
    : [];

  return (
    <BarraFiltros
      chips={chips}
      contagem={contagem}
      aoLimpar={() => {
        irPara({ termo: '' });
      }}
    >
      <div className="min-w-0 flex-1">
        <CampoTexto
          rotulo="Buscar"
          valor={termo}
          placeholder={placeholder}
          atalho="ctrl-k"
          aplicado={Boolean(termo)}
          aoMudar={(novo) => {
            irPara({ termo: novo, substituir: true });
          }}
        />
      </div>
    </BarraFiltros>
  );
}

/** A fatia da pagina atual. A pagina pedida alem da ultima cai na ultima. */
function paginar<T>(linhas: readonly T[], pagina: number, porPagina: number) {
  const paginas = Math.max(Math.ceil(linhas.length / porPagina), 1);
  const atual = Math.min(pagina, paginas);
  const inicio = (atual - 1) * porPagina;
  return { visiveis: linhas.slice(inicio, inicio + porPagina), paginas, atual, inicio };
}

/** Rodape da tabela (p.18): "Mostrando 1–10 de 42", paginacao, itens por pagina. */
function Rodape({
  total,
  fatia,
  porPagina,
  irPara,
}: {
  readonly total: number;
  readonly fatia: { readonly visiveis: readonly unknown[]; readonly paginas: number; readonly atual: number; readonly inicio: number };
  readonly porPagina: number;
  readonly irPara: (estado: EstadoDaBusca) => void;
}) {
  return (
    <RodapeTabela
      primeiro={fatia.inicio + 1}
      ultimo={fatia.inicio + fatia.visiveis.length}
      total={total}
      pagina={fatia.atual}
      paginas={fatia.paginas}
      porPagina={porPagina}
      opcoesPorPagina={OPCOES_POR_PAGINA}
      aoMudarPagina={(nova) => {
        irPara({ pagina: nova });
      }}
      aoMudarPorPagina={(novo) => {
        irPara({ porPagina: novo, pagina: 1 });
      }}
    />
  );
}

/**
 * Uma linha da lista do painel com a sua acao (p.18): a acao fica a direita
 * da PRIMEIRA linha do item (o codigo, o nome), e a segunda linha — a
 * descricao — usa a largura inteira do painel.
 *
 * Antes a acao morava ao lado do bloco de texto inteiro e so ficava
 * transparente: continuava com os seus 140 px ao lado de cada item, e a
 * descricao da permissao era espremida numa coluna de 160 px, em tres ou
 * quatro linhas, para dar lugar a uma coisa invisivel. Flutuar por cima nao
 * serve: no hover ela cobria a descricao. Na linha do codigo ela divide o
 * espaco com um texto curto, e quando nao cabe ao lado dele desce para uma
 * linha propria, alinhada a direita.
 *
 * Visivel no hover e no foco da linha; escondida, nao recebe clique. Onde nao
 * ha ponteiro que paire (toque), sempre a vista — escondida ali, ninguem a
 * acharia.
 */
function LinhaComAcao({
  principal,
  apoio,
  acao,
}: {
  readonly principal: ReactNode;
  readonly apoio: ReactNode;
  readonly acao: ReactNode;
}) {
  return (
    <li className="group flex flex-col gap-1 border-b border-borda-sutil pb-2">
      <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="min-w-0">{principal}</span>
        <span className="ml-auto flex justify-end [@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-focus-within:pointer-events-auto [@media(hover:hover)]:group-focus-within:opacity-100 [@media(hover:hover)]:group-hover:pointer-events-auto [@media(hover:hover)]:group-hover:opacity-100">
          {acao}
        </span>
      </span>
      {apoio}
    </li>
  );
}

/**
 * A lista do painel enquanto chega (p.32), na forma da `LinhaComAcao`: a
 * primeira linha e a segunda, com a mesma divisoria. `segunda` diz se a
 * segunda linha e a descricao (longa) ou um codigo (curta).
 */
function LinhasCarregando({
  oQue,
  itens = 3,
  segunda,
}: {
  readonly oQue: string;
  readonly itens?: number;
  readonly segunda: 'longa' | 'curta';
}) {
  const larguras = segunda === 'longa' ? ['w-full', 'w-4/5', 'w-11/12'] : ['w-24', 'w-32', 'w-28'];
  return (
    <Carregando oQue={oQue} className="flex flex-col gap-2">
      {Array.from({ length: itens }, (_, i) => (
        <div key={i} className="flex flex-col gap-1 border-b border-borda-sutil pb-2">
          <Barra className={`my-1 h-3 ${['w-40', 'w-32', 'w-36'][i % 3] ?? 'w-32'}`} />
          <Barra className={`my-1 h-3 ${larguras[i % 3] ?? 'w-24'}`} />
        </div>
      ))}
    </Carregando>
  );
}

/**
 * Os campos obrigatorios de uma janela de cadastro (p.16 e R01, p.28): o erro
 * aparece EMBAIXO DO CAMPO, AO SAIR dele, dizendo o que fazer. O envio confere
 * de novo so como rede: marca cada campo que falta e leva o foco ao primeiro,
 * em vez de mandar o vazio ao servidor e mostrar a recusa num bloco ao pe do
 * formulario. Quem decide duplicidade e o resto continua sendo o servidor.
 */
function useObrigatorios<Chave extends string>(falta: Readonly<Record<Chave, string>>) {
  const base = useId();
  const [erros, setErros] = useState<Readonly<Record<string, string>>>({});

  const idDe = (campo: Chave) => `${base}-${campo}`;

  function marcar(campo: Chave, mensagem: string | null) {
    setErros((atual) => {
      const resto = Object.fromEntries(Object.entries(atual).filter(([chave]) => chave !== campo));
      return mensagem ? { ...resto, [campo]: mensagem } : resto;
    });
  }

  return {
    idDe,
    erroDe: (campo: Chave): string | null => erros[campo] ?? null,
    /** Ao digitar: para de apontar o problema que a pessoa ja esta consertando. */
    limpar: (campo: Chave) => {
      marcar(campo, null);
    },
    /** Ao sair do campo. */
    conferir: (campo: Chave, valor: string) => {
      marcar(campo, valor.trim() === '' ? falta[campo] : null);
    },
    /** A rede do envio. `true` quando pode gravar. */
    podeEnviar: (valores: Readonly<Record<Chave, string>>): boolean => {
      const faltam = (Object.keys(falta) as Chave[]).filter((campo) => valores[campo].trim() === '');
      if (faltam.length === 0) return true;
      setErros((atual) => ({ ...atual, ...Object.fromEntries(faltam.map((campo) => [campo, falta[campo]])) }));
      const primeiro = faltam[0];
      if (primeiro !== undefined) document.getElementById(idDe(primeiro))?.focus();
      return false;
    },
  };
}

/** Campo de texto obrigatorio com o erro vindo de fora (ver `useObrigatorios`). */
function CampoObrigatorio({
  id,
  rotulo,
  valor,
  erro,
  ajuda,
  aoMudar,
  aoSair,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly valor: string;
  readonly erro: string | null;
  readonly ajuda?: string;
  readonly aoMudar: (valor: string) => void;
  readonly aoSair: (valor: string) => void;
}) {
  return (
    <Campo id={id} rotulo={rotulo} erro={erro} {...(ajuda ? { ajuda } : {})}>
      <input
        id={id}
        type="text"
        autoComplete="off"
        className="controle w-full"
        value={valor}
        onChange={(evento) => {
          aoMudar(evento.target.value);
        }}
        onBlur={(evento) => {
          aoSair(evento.target.value);
        }}
        {...(erro ? { 'aria-invalid': true, 'aria-describedby': `${id}-erro` } : {})}
      />
    </Campo>
  );
}

// ------------------------------------------------------------------- papeis

function VistaDePapeis({ termo, pagina, porPagina, aberto, aoAbrir, aoCadastrar, irPara }: PropsDaLista) {
  const consulta = usePapeis();

  const todos = useMemo(() => consulta.data ?? [], [consulta.data]);
  const linhas = todos.filter((papel) => combinaComTermo([papel.codigo, papel.nome, papel.descricao], termo));
  const fatia = paginar(linhas, pagina, porPagina);

  const colunas: readonly Coluna<Papel>[] = [
    {
      chave: 'codigo',
      rotulo: 'Código',
      celula: (linha) => <span className="codigo text-tinta">{linha.codigo}</span>,
    },
    { chave: 'nome', rotulo: 'Papel', celula: (linha) => linha.nome },
    {
      chave: 'permissoes',
      rotulo: 'Permissões',
      historia: 'COL-CAD-03',
      alinhamento: 'numero',
      celula: (linha) => <span className="numero text-tinta">{formatarInteiro(linha.permissoes)}</span>,
    },
    {
      chave: 'pessoas',
      rotulo: 'Pessoas',
      alinhamento: 'numero',
      celula: (linha) => <span className="numero text-tinta">{formatarInteiro(linha.pessoas)}</span>,
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      alinhamento: 'status',
      celula: (linha) => <Situacao ativo={linha.ativo} />,
    },
  ];

  return (
    <>
      <BarraDeBusca
        termo={termo}
        contagem={
          consulta.isPending ? null : `${formatarInteiro(linhas.length)} de ${formatarInteiro(todos.length)} — papéis`
        }
        placeholder="Código, nome ou descrição do papel"
        irPara={irPara}
      />

      <div data-painel-origem>
        <Tabela
          rotulo="Papéis da empresa"
          colunas={colunas}
          linhas={fatia.visiveis}
          chaveDaLinha={(linha) => linha.id}
          carregando={consulta.isPending}
          selecionada={aberto}
          aoSelecionar={(linha) => {
            aoAbrir(linha.id);
          }}
          erro={
            consulta.isError ? (
              <FalhaAoCarregar
                erro={consulta.error}
                oque="os papéis"
                aoRecarregar={() => {
                  void consulta.refetch();
                }}
              />
            ) : null
          }
          vazio={
            todos.length === 0 ? (
              <EstadoVazio
                titulo="Nenhum papel cadastrado"
                descricao={
                  aoCadastrar
                    ? 'Cadastre o primeiro papel para começar a desenhar quem pode fazer o quê.'
                    : 'Quem cadastra papel é o perfil Administrador. Peça a ele para desenhar quem pode fazer o quê.'
                }
                acao={
                  aoCadastrar ? (
                    <Botao nivel="secundario" onClick={aoCadastrar}>
                      Cadastrar papel
                    </Botao>
                  ) : undefined
                }
              />
            ) : (
              <SemResultado
                titulo="Nenhum papel com esses filtros"
                oque="todos os papéis"
                irPara={irPara}
              />
            )
          }
          rodape={<Rodape total={linhas.length} fatia={fatia} porPagina={porPagina} irPara={irPara} />}
        />
      </div>
    </>
  );
}

function CadastroDePapel({
  id,
  gravar,
  aoConcluir,
}: {
  readonly id: string;
  readonly gravar: ReturnType<typeof useGravarPapel>;
  readonly aoConcluir: () => void;
}) {
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const obrigatorios = useObrigatorios({
    codigo: 'Preencha o código do papel para poder gravar.',
    nome: 'Preencha o nome do papel para poder gravar.',
  });

  return (
    <form
      id={id}
      noValidate
      className="formulario-grade"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!obrigatorios.podeEnviar({ codigo, nome })) return;
        gravar.mutate({ id: null, campos: { codigo: codigo.trim(), nome: nome.trim() } }, { onSuccess: aoConcluir });
      }}
    >
      <CampoObrigatorio
        id={obrigatorios.idDe('codigo')}
        rotulo="Código"
        valor={codigo}
        erro={obrigatorios.erroDe('codigo')}
        ajuda="O mesmo código do perfil na tela de entrada, como gestor-operacional."
        aoMudar={(novo) => {
          obrigatorios.limpar('codigo');
          setCodigo(novo);
        }}
        aoSair={(valor) => {
          obrigatorios.conferir('codigo', valor);
        }}
      />
      <div className="campo-largo">
        <CampoObrigatorio
          id={obrigatorios.idDe('nome')}
          rotulo="Nome do papel"
          valor={nome}
          erro={obrigatorios.erroDe('nome')}
          aoMudar={(novo) => {
            obrigatorios.limpar('nome');
            setNome(novo);
          }}
          aoSair={(valor) => {
            obrigatorios.conferir('nome', valor);
          }}
        />
      </div>
      {gravar.error ? (
        <div className="campo-linha">
          <MensagemDeErro erro={gravar.error} />
        </div>
      ) : null}
    </form>
  );
}

function PainelDoPapel({
  id,
  aoFechar,
  aoDuplicar,
}: {
  readonly id: string;
  readonly aoFechar: () => void;
  readonly aoDuplicar?: (papel: Papel) => void;
}) {
  const consulta = usePapeis();
  const papel = (consulta.data ?? []).find((linha) => linha.id === id) ?? null;
  if (!papel) return null;
  return <DetalheDoPapel key={papel.id} papel={papel} aoFechar={aoFechar} {...(aoDuplicar ? { aoDuplicar } : {})} />;
}

function DetalheDoPapel({
  papel,
  aoFechar,
  aoDuplicar,
}: {
  readonly papel: Papel;
  readonly aoFechar: () => void;
  readonly aoDuplicar?: (papel: Papel) => void;
}) {
  const doPapel = usePermissoesDoPapel(papel.id);
  const todas = usePermissoes();
  const vincular = useVincularPermissao(papel.id);
  const criar = useCriarPermissao();
  const gravarPapel = useGravarPapel();
  /* Conceder, tirar, criar permissão e arquivar: só quem gere a governança. */
  const sessao = useSessao();
  const gere = (sessao.data?.permissoes ?? []).includes('governanca.gerir');

  const [codigoNovo, setCodigoNovo] = useState('');
  const [descricaoNova, setDescricaoNova] = useState('');
  const [moduloNovo, setModuloNovo] = useState('01');

  const concedidas = new Set((doPapel.data ?? []).map((linha) => linha.permissao_id));
  /* 0132 · O papel de posto parceiro só recebe as permissões do lançamento:
     o banco recusa as outras, e a escolha nem as oferece. */
  const disponiveis = permissoesOferecidas(
    papel,
    (todas.data ?? []).filter((permissao) => !concedidas.has(permissao.id)),
  );
  /* O que o papel JA tem, no formato que a regra da leitura le. */
  const semLeitura = acoesSemLeitura(
    (todas.data ?? []).filter((p) => concedidas.has(p.id)),
    todas.data ?? [],
  );

  /* Identidade (p.19): o codigo pequeno em monoespacada, o nome como titulo, e
     embaixo o que o papel e. */
  return (
    <PainelDetalhe
      codigo={papel.codigo}
      titulo={papel.nome}
      subtitulo={papel.descricao.trim() || 'Papel da empresa'}
      marcadores={
        <>
          <Situacao ativo={papel.ativo} />
          {papel.parceiro === true ? <EtiquetaDeParceiro /> : null}
        </>
      }
      dadosChave={
        <>
          <DadoChave rotulo="Permissões">{formatarInteiro(papel.permissoes)}</DadoChave>
          <DadoChave rotulo="Pessoas com o papel">{formatarInteiro(papel.pessoas)}</DadoChave>
          {papel.parceiro === true ? <DadoChave rotulo="Fornecedor">{fornecedorEscrito(papel)}</DadoChave> : null}
        </>
      }
      abas={[
        {
          id: 'permissoes',
          rotulo: 'Permissões',
          conteudo: (
            <div className="flex flex-col gap-4">
              {doPapel.isPending ? (
                <LinhasCarregando oQue="Carregando as permissões do papel" segunda="longa" />
              ) : doPapel.isError ? (
                /* Falha nao e vazio. Sem este ramo, backend fora do ar
                   desenhava "Nenhuma permissao neste papel" e convidava a
                   conceder de novo o que ja estava concedido. */
                <FalhaAoCarregar
                  erro={doPapel.error}
                  oque="as permissões deste papel"
                  aoRecarregar={() => {
                    void doPapel.refetch();
                  }}
                />
              ) : (doPapel.data?.length ?? 0) === 0 ? (
                <EstadoVazio
                  titulo="Nenhuma permissão neste papel"
                  descricao={
                    gere
                      ? 'Conceda a primeira permissão abaixo. Enquanto a matriz não for desenhada, o papel existe e não libera nada.'
                      : 'O papel existe e não libera nada. Quem concede permissão é o perfil Administrador.'
                  }
                />
              ) : (
                <>
                {/* O PAPEL QUE PARECE MONTADO E NAO ABRE NADA (27/10/2026).
                    Sem este aviso, quem concedeu so a acao ve o papel com "1
                    permissao", a pessoa entra e nao acha a tela, e a procura
                    pelo erro comeca no lugar errado — foi o que aconteceu com
                    o lancamento de abastecimento. */}
                {semLeitura.length === 0 ? null : (
                  <div className="flex flex-col gap-1 rounded-card border border-atencao bg-atencao-fundo p-3">
                    {semLeitura.map((f) => (
                      <p key={f.modulo} className="txt-apoio text-pretty text-tinta-secundaria">
                        Este papel tem <span className="codigo text-tinta">{f.acoes.join(', ')}</span> e NAO tem a licenca
                        de ler {rotuloDoModulo(f.modulo)}: quem o recebe pode fazer e nao ve a tela onde se faz. Conceda{' '}
                        {f.leituras.map((l, i) => (
                          <span key={l.id}>
                            {i > 0 ? ' ou ' : ''}
                            <span className="codigo text-tinta">{l.codigo}</span>
                          </span>
                        ))}
                        .
                      </p>
                    ))}
                  </div>
                )}
                <ul className="flex flex-col gap-2">
                  {(doPapel.data ?? []).map((permissao) => (
                    <LinhaComAcao
                      key={permissao.id}
                      principal={<span className="codigo text-tinta">{permissao.codigo}</span>}
                      apoio={<span className="txt-apoio text-pretty text-tinta-secundaria">{permissao.descricao}</span>}
                      acao={
                        gere ? (
                        <BotaoDestrutivo
                          rotulo="Tirar permissão"
                          pergunta={`Tirar a permissão ${permissao.codigo} do papel ${papel.nome}?`}
                          consequencia="Quem tem este papel deixa de ter o que ela libera, a menos que outro papel também a conceda. A retirada fica na trilha, com o seu nome e a data."
                          rotuloConfirmar="Tirar permissão"
                          rotuloCancelar="Manter a permissão"
                          {...(vincular.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
                          pedirMotivo="papel.tirar_permissao"
                          aoConfirmar={(motivo) => {
                            vincular.mutate({ permissaoIds: [permissao.permissao_id], conceder: false, motivo });
                          }}
                        />
                        ) : null
                      }
                    />
                  ))}
                </ul>
                </>
              )}

              {gere ? (
              <div className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
                {papel.parceiro === true ? (
                  <p className="txt-apoio text-pretty text-tinta-secundaria">
                    Papel de posto parceiro: só as permissões do lançamento de combustível (lançar abastecimento, ler o combustível e ler a frota). É gente de fora da empresa.
                  </p>
                ) : null}
                <EscolhaDePermissoes
                  /* Sem as duas listas, "já tem todas" seria dito antes de saber. */
                  {...(todas.isPending || doPapel.isPending ? { carregando: 'Carregando as permissões disponíveis' } : {})}
                  permissoes={disponiveis}
                  todas={todas.data ?? []}
                  jaConcedidas={new Set((doPapel.data ?? []).map((l) => l.codigo))}
                  pendente={vincular.isPending}
                  nomeDoModulo={rotuloDoModulo}
                  aoConceder={(ids, concluido) => {
                    vincular.mutate({ permissaoIds: ids, conceder: true }, { onSuccess: concluido });
                  }}
                />
                <MensagemDeErro erro={vincular.error} />
              </div>
              ) : null}
            </div>
          ),
        },
        /* 0128 · A TRAVA DO LANÇAMENTO DE ABASTECIMENTO: só no papel que lança
           (o que tem `abastecimento.registrar`), e nunca no Administrador, que
           tem acesso a tudo por decisão do dono. */
        /* 0132 · NO PAPEL DE PARCEIRO a trava não se edita (só externo, no
           posto do fornecedor): a aba diz isso e escolhe o fornecedor do papel. */
        ...(papel.parceiro === true
          ? [
              {
                id: 'trava',
                rotulo: 'Trava',
                conteudo: (
                  <div className="flex flex-col gap-3">
                    <h3 className="txt-subtitulo text-tinta">Lançamento do posto parceiro</h3>
                    <p className="txt-corpo text-tinta">{TRAVA_DO_PARCEIRO}</p>
                    <FornecedorDoPapel key={papel.versao ?? papel.id} papel={papel} gere={gere} />
                  </div>
                ),
              },
            ]
          : papel.lanca_abastecimento === true && papel.codigo !== 'administrador'
          ? [
              {
                id: 'trava',
                /* "Trava", curto: com "Permissões" e "Nova permissão", o
                   rótulo longo quebrava o trilho de abas no painel de 360 px. */
                rotulo: 'Trava',
                conteudo: (
                  <TravaDoLancamento
                    key={papel.versao ?? papel.id}
                    papel={papel}
                    gere={gere}
                    leCombustivel={(sessao.data?.permissoes ?? []).includes('combustivel.ler')}
                  />
                ),
              },
            ]
          : []),
        /* 0130 · O INÍCIO DO APP (decisão 55): em todo papel, menos no
           Administrador, que abre sempre no painel da empresa. */
        ...(papel.codigo !== 'administrador'
          ? [
              {
                id: 'inicio',
                rotulo: 'Início',
                conteudo: <InicioDoPapel key={papel.versao ?? papel.id} papel={papel} gere={gere} />,
              },
            ]
          : []),
        ...(gere ? [{
          id: 'nova',
          rotulo: 'Nova permissão',
          conteudo: (
            <form
              className="flex flex-col gap-3"
              onSubmit={(evento) => {
                evento.preventDefault();
                criar.mutate(
                  {
                    codigo: codigoNovo.trim(),
                    descricao: descricaoNova.trim(),
                    modulo: moduloNovo,
                  },
                  {
                    onSuccess: () => {
                      setCodigoNovo('');
                      setDescricaoNova('');
                    },
                  },
                );
              }}
            >
              <p className="txt-apoio text-tinta-secundaria">
                Cadastre aqui a permissão que um módulo novo precisar. Depois de cadastrada, ela pode ser concedida a qualquer papel.
              </p>
              <CampoTexto
                rotulo="Código"
                valor={codigoNovo}
                ajuda="No formato recurso.acao, em minúsculas: abastecimento.registrar."
                validar={(valor) =>
                  valor.trim() === '' || /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/.test(valor.trim())
                    ? null
                    : 'Use o formato recurso.acao, em minúsculas: abastecimento.registrar.'
                }
                aoMudar={setCodigoNovo}
              />
              <CampoTexto rotulo="O que ela libera" valor={descricaoNova} aoMudar={setDescricaoNova} />
              <CampoSelecao
                rotulo="Módulo"
                valor={moduloNovo}
                opcoes={MODULOS_DO_V6.map((m) => ({ valor: m.numero, rotulo: rotuloDoModulo(m.numero) }))}
                aoMudar={setModuloNovo}
              />
              <MensagemDeErro erro={criar.error} />
              <Botao nivel="secundario" type="submit" disabled={criar.isPending}>
                Cadastrar permissão
              </Botao>
            </form>
          ),
        }] : []),
      ]}
      notaDasAcoes="Arquivar o papel não tira as permissões nem os vínculos: ele deixa de aparecer nas listas de escolha, e o histórico fica."
      acoes={gere ? [
        /* 0132 · Duplicar: o próximo posto (e o próximo colaborador) a partir
           de um papel pronto. */
        ...(aoDuplicar
          ? [
              <Botao
                key="duplicar"
                nivel="secundario"
                onClick={() => {
                  aoDuplicar(papel);
                }}
              >
                Duplicar papel
              </Botao>,
            ]
          : []),
        papel.ativo ? (
          <BotaoDestrutivo
            key="arquivar"
            rotulo="Arquivar papel"
            pergunta={`Arquivar o papel ${papel.nome}?`}
            consequencia="Ele sai das listas de escolha. As permissões e as pessoas que já o têm continuam como estão, e o histórico fica."
            rotuloConfirmar="Arquivar papel"
            rotuloCancelar="Manter o papel"
            {...(gravarPapel.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
            pedirMotivo="papel.desativar"
            aoConfirmar={(motivo) => {
              gravarPapel.mutate({ id: papel.id, campos: { ativo: false, motivo } });
            }}
          />
        ) : (
          <Botao
            key="reabrir"
            nivel="secundario"
            onClick={() => {
              gravarPapel.mutate({ id: papel.id, campos: { ativo: true } });
            }}
          >
            Reabrir papel
          </Botao>
        ),
      ] : []}
      aoFechar={aoFechar}
    />
  );
}

// ------------------------------------------------------------------ pessoas

function VistaDePessoas({ termo, pagina, porPagina, aberto, aoAbrir, aoCadastrar, irPara }: PropsDaLista) {
  const consulta = usePessoas();

  const pessoas = consulta.data?.itens ?? [];
  const vinculos = consulta.data?.papeis ?? [];
  const linhas = pessoas.filter((pessoa) => combinaComTermo([pessoa.nome, pessoa.email], termo));
  const fatia = paginar(linhas, pagina, porPagina);

  const papeisDe = (id: string) => vinculos.filter((vinculo) => vinculo.usuario_id === id);

  /* O E-MAIL e a segunda linha da pessoa (p.18: segunda linha em 12 px,
     apagada), e nao uma coluna: e-mail nao tem onde quebrar, e numa coluna
     propria ele empurrava o Acesso para fora do quadro a 1280 e espremia o
     nome em duas linhas. A FILIAL e secundaria (R01, p.28): sai em M e com o
     quadro estreito, e continua nos dados-chave do painel. A frase do acesso
     vai a uma linha na tabela, com o texto inteiro no `title` e na aba Acesso
     do painel; no cartao, inteira. */
  const colunas: readonly Coluna<Pessoa>[] = [
    {
      chave: 'nome',
      rotulo: 'Pessoa',
      celula: (linha) => (
        <span className="flex min-w-0 flex-col">
          <TextoDaCelula texto={linha.nome} linhas={1} />
          {linha.email ? (
            <TextoDaCelula texto={linha.email} classe="txt-apoio text-tinta-apagada" linhas={1} />
          ) : (
            <span className="txt-apoio text-tinta-secundaria">Sem e-mail cadastrado</span>
          )}
        </span>
      ),
    },
    {
      chave: 'filial',
      rotulo: 'Filial',
      prioridade: 'secundaria',
      celula: (linha) =>
        linha.filial_nome ? (
          <TextoDaCelula texto={linha.filial_nome} />
        ) : (
          <span className="text-tinta-secundaria">Sem filial</span>
        ),
    },
    {
      chave: 'papeis',
      rotulo: 'Papéis',
      celula: (linha) => {
        const seus = papeisDe(linha.id);
        /* A conta de suporte (0134, decisao 62): o papel continua o que e,
           com tudo o que concede; o nome que a empresa le e "Suporte". */
        if (linha.suporte === true) return <TextoDaCelula texto={ROTULO_DE_SUPORTE} />;
        return seus.length === 0 ? (
          <span className="text-tinta-secundaria">Nenhum papel</span>
        ) : (
          /* O alcance vai junto quando nao e a empresa inteira (0098, M38). */
          <TextoDaCelula
            texto={seus
              .map((vinculo) => {
                const alcance = alcanceEscrito(vinculo);
                return alcance === 'Empresa inteira' ? vinculo.papel_nome : `${vinculo.papel_nome} (${minusculaInicial(alcance)})`;
              })
              .join(', ')}
          />
        );
      },
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      alinhamento: 'status',
      celula: (linha) => <SituacaoDePessoa ativo={linha.ativo} />,
    },
    {
      chave: 'acesso',
      rotulo: 'Acesso',
      celula: (linha) => {
        const acesso = situacaoDoAcesso(linha, new Date());
        return (
          <span className="flex min-w-0 flex-col">
            <span className="txt-corpo whitespace-nowrap text-tinta">{acesso.rotulo}</span>
            <TextoDaCelula texto={acesso.detalhe} classe="txt-apoio text-tinta-apagada" linhas={1} />
          </span>
        );
      },
    },
  ];

  return (
    <>
      <BarraDeBusca
        termo={termo}
        contagem={
          consulta.isPending ? null : `${formatarInteiro(linhas.length)} de ${formatarInteiro(pessoas.length)} — pessoas`
        }
        placeholder="Nome ou e-mail"
        irPara={irPara}
      />

      <div data-painel-origem>
        <Tabela
          rotulo="Pessoas da empresa"
          colunas={colunas}
          linhas={fatia.visiveis}
          alturaDupla
          chaveDaLinha={(linha) => linha.id}
          carregando={consulta.isPending}
          selecionada={aberto}
          aoSelecionar={(linha) => {
            aoAbrir(linha.id);
          }}
          erro={
            consulta.isError ? (
              <FalhaAoCarregar
                erro={consulta.error}
                oque="as pessoas"
                aoRecarregar={() => {
                  void consulta.refetch();
                }}
              />
            ) : null
          }
          vazio={
            pessoas.length === 0 ? (
              <EstadoVazio
                titulo="Nenhuma pessoa cadastrada"
                descricao={
                  aoCadastrar
                    ? 'Cadastre a primeira pessoa para poder conceder papéis e registrar quem aprova compra, ajuste, O.S. e liberação.'
                    : 'Quem cadastra pessoa é o perfil Administrador. Peça a ele para cadastrar quem usa o sistema.'
                }
                acao={
                  aoCadastrar ? (
                    <Botao nivel="secundario" onClick={aoCadastrar}>
                      Cadastrar pessoa
                    </Botao>
                  ) : undefined
                }
              />
            ) : (
              <SemResultado titulo="Nenhuma pessoa com esses filtros" oque="todas as pessoas" irPara={irPara} />
            )
          }
          rodape={<Rodape total={linhas.length} fatia={fatia} porPagina={porPagina} irPara={irPara} />}
        />
      </div>
    </>
  );
}

function CadastroDePessoa({
  id,
  criar,
  aoConcluir,
}: {
  readonly id: string;
  readonly criar: ReturnType<typeof useCriarPessoa>;
  readonly aoConcluir: () => void;
}) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const obrigatorios = useObrigatorios({ nome: 'Preencha o nome da pessoa para poder gravar.' });

  return (
    <form
      id={id}
      noValidate
      className="formulario-grade"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!obrigatorios.podeEnviar({ nome })) return;
        criar.mutate(
          {
            nome: nome.trim(),
            ...(email.trim() ? { email: email.trim() } : {}),
            motivo: 'cadastro de pessoa pela tela',
          },
          { onSuccess: aoConcluir },
        );
      }}
    >
      {/* O que antes era uma segunda nota azul acima da lista: e aqui, na hora
          de cadastrar, que a pessoa precisa saber que o cadastro nao da senha. */}
      <p className="txt-apoio campo-linha text-tinta-secundaria">
        A pessoa nasce na sua empresa, sem senha. Para ela entrar, abra a pessoa e emita o convite: ela recebe o endereço
        de você e escolhe a própria senha.
      </p>
      <div className="campo-largo">
        <CampoObrigatorio
          id={obrigatorios.idDe('nome')}
          rotulo="Nome da pessoa"
          valor={nome}
          erro={obrigatorios.erroDe('nome')}
          aoMudar={(novo) => {
            obrigatorios.limpar('nome');
            setNome(novo);
          }}
          aoSair={(valor) => {
            obrigatorios.conferir('nome', valor);
          }}
        />
      </div>
      <CampoTexto
        rotulo="E-mail"
        valor={email}
        opcional
        ajuda="É o e-mail que a pessoa digita para entrar no sistema, depois de aceitar o convite."
        aoMudar={setEmail}
      />
      {criar.error ? (
        <div className="campo-linha">
          <MensagemDeErro erro={criar.error} />
        </div>
      ) : null}
    </form>
  );
}

function PainelDaPessoa({ id, aoFechar }: { readonly id: string; readonly aoFechar: () => void }) {
  const consulta = usePessoas();
  const papeis = usePapeis();
  const pessoa = (consulta.data?.itens ?? []).find((linha) => linha.id === id) ?? null;
  if (!pessoa) return null;
  /* Um VINCULO por alcance (0098): o mesmo papel pode vir duas vezes, numa
     obra e num contrato. A tela lista e tira o vinculo, e nao o papel. */
  const vinculos = (consulta.data?.papeis ?? []).filter((vinculo) => vinculo.usuario_id === pessoa.id);
  return (
    <DetalheDaPessoa
      key={pessoa.id}
      pessoa={pessoa}
      vinculos={vinculos}
      todosOsPapeis={papeis.data ?? []}
      carregandoPapeis={papeis.isPending}
      aoFechar={aoFechar}
    />
  );
}

const ROTULO_DO_ALCANCE: Readonly<Record<TipoDeAlcance, string>> = {
  empresa: 'Empresa inteira',
  filial: 'Filial',
  obra: 'Obra',
  contrato: 'Contrato',
  fornecedor: 'Fornecedor',
};

function DetalheDaPessoa({
  pessoa,
  vinculos,
  todosOsPapeis,
  carregandoPapeis,
  aoFechar,
}: {
  readonly pessoa: Pessoa;
  /** Os papeis concedidos, um por alcance, com a vigencia (0081, 0083, 0098). */
  readonly vinculos: readonly PapelDaPessoa[];
  readonly todosOsPapeis: readonly Papel[];
  /** A lista de papeis ainda vem: o nome dos concedidos e as opcoes dependem dela. */
  readonly carregandoPapeis: boolean;
  readonly aoFechar: () => void;
}) {
  const vincular = useVincularPapel(pessoa.id);
  const idDaConcessao = useId();
  /* Conceder e tirar papel: só quem gere a governança. */
  const sessao = useSessao();
  const gere = (sessao.data?.permissoes ?? []).includes('governanca.gerir');
  const { filiais } = useReferencias();
  const obras = useCadastro('obras');
  const contratos = useCadastro('contratos');
  /* 0123 · o alcance do posto parceiro. A lista so e pedida quando o papel
     escolhido e o dele: quem concede um papel da empresa nao espera por ela. */
  const [papelEscolhido, setPapelEscolhido] = useState('');
  /* CONTRATO DE 30/10/2026 (0132): o posto parceiro e o papel com `parceiro`,
     e nao o de codigo 'posto-parceiro' (a copia do papel e parceiro tambem).
     O papel de UM posto traz o fornecedor fixo; o generico pede a escolha. */
  const concessao = fornecedorDaConcessao(todosOsPapeis.find((p) => p.id === papelEscolhido));
  const ehPostoParceiro = concessao.modo !== 'nenhum';
  const fornecedores = useCadastro('fornecedores', concessao.modo === 'escolher');
  const [tipo, setTipo] = useState<TipoDeAlcance>('empresa');
  const [lugar, setLugar] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');

  /* O mesmo papel pode valer em outro alcance (0098): a lista oferece todos os
     papeis em uso; repetir o MESMO alcance o servidor recusa com o que fazer. */
  const disponiveis = todosOsPapeis.filter((papel) => papel.ativo);
  const nomeDoPapel = new Map(todosOsPapeis.map((p) => [p.id, p]));
  const listaDoLugar =
    tipo === 'filial' ? filiais : tipo === 'obra' ? obras : tipo === 'contrato' ? contratos : tipo === 'fornecedor' ? fornecedores : null;
  const opcoesDoLugar =
    concessao.modo === 'fixo' && tipo === 'fornecedor'
      ? [{ valor: concessao.id, rotulo: concessao.nome }]
      : (listaDoLugar?.data ?? [])
          .filter((l) => l.ativo)
          .map((l) => ({ valor: l.id, rotulo: tipo === 'contrato' ? `${l.codigo} · ${l.descricao ?? l.nome ?? ''}` : rotuloDe(l) }));
  const lugarEscolhido = opcoesDoLugar.find((o) => o.valor === lugar);
  const faltaLugar = tipo !== 'empresa' && !lugarEscolhido;
  const fimAntes = fim !== '' && inicio !== '' && fim < inicio;
  const alcanceDoPedido =
    tipo === 'empresa'
      ? 'Empresa inteira'
      : `Só ${tipo === 'filial' ? 'na filial' : tipo === 'obra' ? 'na obra' : tipo === 'fornecedor' ? 'no fornecedor' : 'no contrato'} ${lugarEscolhido?.rotulo ?? ''}`.trim();

  return (
    <PainelDetalhe
      titulo={pessoa.nome}
      subtitulo={pessoa.email ?? 'Sem e-mail cadastrado'}
      marcadores={<SituacaoDePessoa ativo={pessoa.ativo} />}
      dadosChave={
        <>
          <DadoChave rotulo="Filial">{pessoa.filial_nome ?? 'Sem filial'}</DadoChave>
          <DadoChave rotulo="Papéis">{formatarInteiro(vinculos.length)}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'acesso',
          rotulo: 'Acesso',
          conteudo: <AcessoDaPessoa pessoa={pessoa} />,
        },
        {
          id: 'situacao',
          rotulo: 'Situação e sessões',
          conteudo: <SituacaoESessoes pessoa={pessoa} />,
        },
        {
          id: 'papeis',
          rotulo: 'Papéis',
          conteudo: (
            <div className="flex flex-col gap-4">
              {vinculos.length === 0 ? (
                <EstadoVazio
                  titulo="Nenhum papel concedido"
                  descricao={
                    gere
                      ? 'Conceda o primeiro papel abaixo. Ele fica registrado na trilha com autor, data e motivo.'
                      : 'Sem papel, a pessoa entra e não vê módulo nenhum. Quem concede papel é o perfil Administrador.'
                  }
                />
              ) : carregandoPapeis ? (
                /* Quantos ja se sabe pelo vinculo; o nome vem da lista de papeis. */
                <LinhasCarregando oQue="Carregando os papéis da pessoa" itens={vinculos.length} segunda="curta" />
              ) : (
                <ul className="flex flex-col gap-2">
                  {vinculos.map((vinculo) => {
                    const papel = nomeDoPapel.get(vinculo.papel_id);
                    const nome = papel?.nome ?? vinculo.papel_nome;
                    const alcance = alcanceEscrito(vinculo);
                    return (
                      <LinhaComAcao
                        key={vinculo.id}
                        principal={<span className="txt-corpo text-tinta">{nome}</span>}
                        apoio={
                          <span className="txt-apoio text-tinta-secundaria">
                            <span className="codigo">{papel?.codigo ?? vinculo.papel_codigo}</span>
                            {` · ${alcance} · ${vigenciaEscrita(vinculo)}`}
                          </span>
                        }
                        acao={
                          gere ? (
                          <BotaoDestrutivo
                            rotulo="Tirar papel"
                            pergunta={`Tirar o papel ${nome} (${minusculaInicial(alcance)}) de ${pessoa.nome}?`}
                            consequencia="As permissões deste papel, neste alcance, deixam de valer para a pessoa, a menos que outro papel dela também as conceda. O mesmo papel em outro alcance continua. A retirada fica na trilha, com o seu nome e a data."
                            rotuloConfirmar="Tirar papel"
                            rotuloCancelar="Manter o papel"
                            {...(vincular.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
                            pedirMotivo="pessoa.tirar_papel"
                            aoConfirmar={(motivo) => {
                              vincular.mutate({ papelId: vinculo.papel_id, vinculoId: vinculo.id, conceder: false, motivo, alcance });
                            }}
                          />
                          ) : null
                        }
                      />
                    );
                  })}
                </ul>
              )}

              {/* 0128 · o que VALE para a pessoa no lançamento de
                  abastecimento: a soma dos papéis dela que lançam. */}
              <TravaDaPessoa
                pessoa={pessoa}
                papeisDaPessoa={[
                  ...new Set(vinculos.filter((v) => v.vigente !== false).map((v) => v.papel_id)),
                ].flatMap((idDoPapel) => {
                  const papel = nomeDoPapel.get(idDoPapel);
                  return papel && papel.ativo ? [papel] : [];
                })}
                leCombustivel={(sessao.data?.permissoes ?? []).includes('combustivel.ler')}
              />

              {gere ? (
              <div className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
                {/* O ALCANCE (0081, 0083, 0098): empresa, filial, obra ou
                    contrato — a mesma escolha do celular (M40). O aviso fica
                    ao lado do ato que ele explica. */}
                {/* NO POSTO PARCEIRO NAO HA ESCOLHA DE ALCANCE (0123): ele e
                    por fornecedor, sempre, e um seletor com uma opcao so e uma
                    pergunta cuja resposta ja se sabe. No lugar dele, a frase
                    que diz por que o campo abaixo nao e opcional. */}
                {ehPostoParceiro ? (
                  <p className="txt-apoio rounded-controle bg-atencao-fundo px-4 py-2 text-atencao-texto">
                    Este acesso e de fora da empresa. Ele vale por fornecedor, e o fornecedor e obrigatorio: e por ele
                    que o sistema limita o que o posto enxerga.
                  </p>
                ) : (
                  <CampoSelecao
                    rotulo="Alcance"
                    valor={tipo}
                    opcoes={(['empresa', 'filial', 'obra', 'contrato'] as const).map((t) => ({ valor: t, rotulo: ROTULO_DO_ALCANCE[t] }))}
                    aoMudar={(t) => {
                      setTipo(t as TipoDeAlcance);
                      setLugar('');
                    }}
                  />
                )}
                {concessao.modo === 'fixo' ? (
                  <Campo id={`${idDaConcessao}-fornecedor`} rotulo="Fornecedor" ajuda="Fornecedor do papel.">
                    <input id={`${idDaConcessao}-fornecedor`} className="controle" value={concessao.nome} readOnly aria-readonly="true" />
                  </Campo>
                ) : tipo === 'empresa' ? null : listaDoLugar?.isPending ? (
                  <div className="flex min-w-0 flex-col gap-2">
                    <span className="txt-rotulo-campo">{ROTULO_DO_ALCANCE[tipo]}</span>
                    <ControleCarregando oQue={`Carregando ${tipo === 'filial' ? 'as filiais' : tipo === 'obra' ? 'as obras' : tipo === 'fornecedor' ? 'os fornecedores' : 'os contratos'}`} />
                  </div>
                ) : (
                  <CampoSelecao
                    rotulo={ROTULO_DO_ALCANCE[tipo]}
                    valor={lugar}
                    rotuloVazio={
                      listaDoLugar?.isError
                        ? 'Não foi possível carregar a lista. Feche e abra a pessoa de novo.'
                        : opcoesDoLugar.length === 0
                          ? `Nenhum${tipo === 'filial' || tipo === 'obra' ? 'a' : ''} ${ROTULO_DO_ALCANCE[tipo].toLocaleLowerCase('pt-BR')} em uso`
                          : `Escolha ${tipo === 'contrato' ? 'o contrato' : tipo === 'obra' ? 'a obra' : tipo === 'fornecedor' ? 'o fornecedor' : 'a filial'}`
                    }
                    opcoes={opcoesDoLugar}
                    aoMudar={setLugar}
                  />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Campo id={`${idDaConcessao}-inicio`} rotulo="Início" opcional>
                    <input
                      id={`${idDaConcessao}-inicio`}
                      type="date"
                      className="controle"
                      value={inicio}
                      onChange={(e) => {
                        setInicio(e.target.value);
                      }}
                    />
                  </Campo>
                  <Campo id={`${idDaConcessao}-fim`} rotulo="Fim" opcional>
                    <input
                      id={`${idDaConcessao}-fim`}
                      type="date"
                      className="controle"
                      value={fim}
                      min={inicio || undefined}
                      onChange={(e) => {
                        setFim(e.target.value);
                      }}
                    />
                  </Campo>
                </div>
                {fimAntes ? (
                  <p role="alert" className="txt-apoio text-parada-texto">
                    O fim vem depois do início. Corrija o dia do fim ou deixe em branco.
                  </p>
                ) : null}
                <DesenhoDaConcessao
                  escolha={papelEscolhido}
                  aoEscolher={(valor) => {
                    /* Trocar de papel refaz o alcance: o posto parceiro so
                       aceita fornecedor, e os outros nao aceitam nenhum. */
                    setPapelEscolhido(valor);
                    const doPapel = fornecedorDaConcessao(todosOsPapeis.find((pp) => pp.id === valor));
                    setTipo(doPapel.modo === 'nenhum' ? 'empresa' : 'fornecedor');
                    setLugar(doPapel.modo === 'fixo' ? doPapel.id : '');
                  }}
                  rotulo="Conceder papel"
                  {...(carregandoPapeis ? { carregando: 'Carregando os papéis disponíveis' } : {})}
                  rotuloVazio={disponiveis.length === 0 ? 'Nenhum disponível' : 'Escolha um papel'}
                  opcoes={disponiveis.map((papel) => ({ valor: papel.id, rotulo: papel.nome }))}
                  pendente={vincular.isPending || faltaLugar || fimAntes}
                  nota={`${O_QUE_O_ALCANCE_VE[tipo]} Sem início, o papel vale desde hoje; sem fim, não vence. Conceder libera na hora as permissões do papel.`}
                  aoConceder={(valor, concluido) => {
                    vincular.mutate(
                      {
                        papelId: valor,
                        conceder: true,
                        filialId: tipo === 'filial' ? lugar : null,
                        obraId: tipo === 'obra' ? lugar : null,
                        contratoId: tipo === 'contrato' ? lugar : null,
                        fornecedorId: tipo === 'fornecedor' ? lugar : null,
                        inicio: inicio || null,
                        fim: fim || null,
                        alcance: alcanceDoPedido,
                      },
                      {
                        onSuccess: () => {
                          concluido();
                          setInicio('');
                          setFim('');
                        },
                      },
                    );
                  }}
                />
                <MensagemDeErro erro={vincular.error} />
              </div>
              ) : null}
            </div>
          ),
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}

/** "Só no contrato CTR-2026-01" vira "só no contrato CTR-2026-01": o código fica como é. */
function minusculaInicial(texto: string): string {
  return texto.charAt(0).toLocaleLowerCase('pt-BR') + texto.slice(1);
}

/** "desde 01/08/2026", "a partir de 10/10/2026 até 31/12/2026", "venceu em 30/09/2026" (0083). */
function vigenciaEscrita(v: PapelDaPessoa): string {
  const hoje = diaDaOperacao();
  const inicio = v.inicio ?? v.concedido_em.slice(0, 10);
  if (v.fim && v.fim < hoje) return `venceu em ${formatarDia(v.fim)}`;
  const ate = v.fim ? ` até ${formatarDia(v.fim)}` : ', sem fim';
  return `${inicio > hoje ? 'a partir de' : 'desde'} ${formatarDia(inicio)}${ate}`;
}

/**
 * O convite de primeiro acesso (migração 0024). O endereço aparece UMA vez,
 * logo depois de emitido: o banco guarda só o hash do código, e nem esta tela
 * consegue mostrá-lo de novo. Perdeu, emite outro — o anterior deixa de valer.
 */
function AcessoDaPessoa({ pessoa }: { readonly pessoa: Pessoa }) {
  const idEndereco = useId();
  const sessao = useSessao();
  const podeConvidar = (sessao.data?.permissoes ?? []).includes('acesso.convidar');
  const emitir = useEmitirConvite(pessoa.id);
  /* Como o convite chega (0097): por e-mail, se o servidor tem o envio ligado. */
  const entrega = useEntregaDoConvite(podeConvidar);
  const [emitido, setEmitido] = useState<ConviteEmitido | null>(null);
  const [copiado, setCopiado] = useState<'sim' | 'falhou' | null>(null);
  const acesso = situacaoDoAcesso(pessoa, new Date());
  const envio = pessoa.convite_ate ? situacaoDoEnvio(pessoa) : null;
  const endereco = emitido ? enderecoDoConvite(window.location.origin, emitido.codigo) : '';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <span className="txt-corpo text-tinta">{acesso.rotulo}</span>
        <span className="txt-apoio text-tinta-secundaria">{acesso.detalhe}</span>
      </div>

      {envio ? (
        <div className="flex flex-col gap-1">
          <span className="txt-corpo text-tinta">{envio.rotulo}</span>
          <span className="txt-apoio text-tinta-secundaria">{envio.detalhe}</span>
        </div>
      ) : null}

      {emitido ? (
        <div className="cartao flex flex-col gap-2 p-4">
          <label htmlFor={idEndereco} className="txt-rotulo-campo">
            Endereço do convite
          </label>
          <input
            id={idEndereco}
            readOnly
            className="controle codigo"
            value={endereco}
            onFocus={(evento) => {
              evento.target.select();
            }}
          />
          <p className="txt-apoio text-tinta-secundaria">
            {emitido.entrega === 'email'
              ? `O mesmo endereço vai por e-mail para ${pessoa.email ?? pessoa.nome}. Se o e-mail não chegar, entregue este a ${pessoa.nome}: ele aparece só agora, vale uma vez e até ${formatarDataHora(new Date(emitido.expiraEm))}.`
              : `Entregue este endereço a ${pessoa.nome}. Ele aparece só agora, vale uma vez e até ${formatarDataHora(new Date(emitido.expiraEm))}. Quem o abrir escolhe a senha desta pessoa, então entregue só a ela.`}
          </p>
          <div className="flex items-center gap-3">
            <Botao
              nivel="secundario"
              onClick={() => {
                navigator.clipboard.writeText(endereco).then(
                  () => {
                    setCopiado('sim');
                  },
                  () => {
                    setCopiado('falhou');
                  },
                );
              }}
            >
              Copiar endereço
            </Botao>
            {copiado === 'sim' ? <span className="txt-apoio text-tinta-secundaria">Endereço copiado.</span> : null}
            {copiado === 'falhou' ? (
              <span className="txt-apoio text-tinta-secundaria">Não foi possível copiar. Selecione o endereço e copie à mão.</span>
            ) : null}
          </div>
        </div>
      ) : null}

      {podeConvidar ? (
        <div className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
          <Botao
            nivel="secundario"
            disabled={emitir.isPending || !pessoa.ativo || !pessoa.email}
            onClick={() => {
              setCopiado(null);
              emitir.mutate(undefined, {
                onSuccess: (convite) => {
                  setEmitido(convite);
                },
              });
            }}
          >
            {pessoa.tem_acesso ? 'Emitir troca de senha' : 'Emitir convite'}
          </Botao>
          <p className="txt-apoio text-tinta-secundaria">
            {[
              pessoa.convite_ate ? 'Emitir outro anula o convite aberto.' : null,
              entrega.data ? fraseDaEntrega(entrega.data.porEmail, pessoa.email) : null,
            ]
              .filter(Boolean)
              .join(' ') || 'A pessoa abre o endereço, escolhe a senha e entra. Você não fica sabendo a senha.'}
          </p>
          <MensagemDeErro erro={emitir.error} />
        </div>
      ) : (
        <p className="txt-apoio text-tinta-secundaria">Só quem tem a permissão de convidar emite convite. Peça ao administrador do sistema.</p>
      )}
    </div>
  );
}

/** Uma sessão aberta da pessoa (M39): o `id` é um resumo do token, nunca o token. */
interface SessaoAberta {
  readonly id: string;
  readonly aparelho: string | null;
  readonly criadaEm: string;
  readonly ultimoUsoEm: string | null;
}

/* A mesma chave do app (src/mobile/administracao.ts): as duas telas dividem o cache. */
const chaveDasSessoes = (pessoaId: string) => ['cadastros', 'usuarios', pessoaId, 'sessoes'] as const;

/**
 * Situação e sessões da pessoa — o que o app faz no M39 e no M44, na mesa:
 * as sessões abertas com o aparelho, encerrar todas, e desativar ou reativar.
 * Desativar derruba as sessões no servidor (`infra.encerrar_sessoes_da_pessoa`);
 * os lançamentos da pessoa continuam na trilha.
 */
function SituacaoESessoes({ pessoa }: { readonly pessoa: Pessoa }) {
  const cliente = useQueryClient();
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const podeGerir = permissoes.includes('governanca.gerir');
  const eu = sessao.data?.usuarioId === pessoa.id;
  const gravar = useGravarPessoa();
  const sessoes = useQuery({
    queryKey: chaveDasSessoes(pessoa.id),
    enabled: pessoa.ativo,
    queryFn: async () => (await pedir<{ sessoes: readonly SessaoAberta[] }>(`/cadastros/usuarios/${encodeURIComponent(pessoa.id)}/sessoes`)).sessoes,
  });
  const encerrar = useMutation({
    mutationFn: (motivo: string) =>
      enviarJson<{ encerradas: number }>(`/cadastros/usuarios/${encodeURIComponent(pessoa.id)}/sessoes/encerramento`, 'POST', { motivo }),
    meta: {
      retorno: retornoDe<{ encerradas: number }>((r) => ({
        titulo: 'Sessões encerradas',
        alvo: { nome: pessoa.nome },
        fatos: [`${formatarInteiro(r.encerradas)} ${r.encerradas === 1 ? 'sessão' : 'sessões'}`],
        efeito: 'Para voltar, a pessoa entra de novo com a senha',
      })),
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['cadastros'] });
    },
  });
  const abertas = sessoes.data ?? [];
  const primeiroNome = pessoa.nome.split(/\s+/)[0] ?? pessoa.nome;
  const nosAparelhos = abertas.length === 1 ? 'no aparelho' : `nos ${formatarInteiro(abertas.length)} aparelhos`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="txt-rotulo text-tinta">Sessões abertas</span>
        {!pessoa.ativo ? (
          <p className="txt-apoio text-tinta-secundaria">Pessoa desativada não tem sessão aberta: as dela caíram ao desativar.</p>
        ) : sessoes.isPending ? (
          <LinhasCarregando oQue="Carregando as sessões da pessoa" itens={2} segunda="curta" />
        ) : sessoes.isError ? (
          <div role="alert" className="flex flex-col gap-2">
            <MensagemDeErro erro={sessoes.error} />
            <div>
              <Botao
                nivel="secundario"
                onClick={() => {
                  void sessoes.refetch();
                }}
              >
                Carregar de novo
              </Botao>
            </div>
          </div>
        ) : abertas.length === 0 ? (
          <p className="txt-apoio text-tinta-secundaria">Nenhuma sessão aberta: a pessoa não está conectada em nenhum aparelho.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {abertas.map((s) => (
              <li key={s.id} className="flex flex-col border-b border-borda-sutil py-2">
                <span className="txt-corpo text-tinta">{s.aparelho ?? 'Aparelho sem nome'}</span>
                <span className="txt-apoio tabular-nums text-tinta-secundaria">
                  {`Aberta em ${formatarDataHora(new Date(s.criadaEm))}`}
                  {s.ultimoUsoEm ? ` · último uso em ${formatarDataHora(new Date(s.ultimoUsoEm))}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
        {podeGerir && !eu && pessoa.ativo && abertas.length > 0 ? (
          <div>
            <BotaoDestrutivo
              rotulo="Encerrar sessões"
              pergunta={`Encerrar as sessões de ${pessoa.nome}?`}
              consequencia={`${primeiroNome} sai do sistema ${nosAparelhos} na hora e entra de novo com a senha. Nada do que foi lançado se perde.`}
              rotuloConfirmar="Encerrar sessões"
              rotuloCancelar="Manter sessões"
              {...(encerrar.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
              pedirMotivo="pessoa.encerrar_sessoes"
              aoConfirmar={(motivo) => {
                encerrar.mutate(motivo);
              }}
            />
          </div>
        ) : null}
        <MensagemDeErro erro={encerrar.error} />
      </div>

      <div className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
        <span className="txt-rotulo text-tinta">Situação</span>
        <p className="txt-apoio text-tinta-secundaria">
          {pessoa.ativo
            ? 'Desativar derruba as sessões abertas e anula o convite pendente. Os lançamentos da pessoa continuam na trilha, e dá para reativar depois.'
            : 'Reativada, a pessoa volta a entrar com a senha que já tinha. Se não tiver, emita o convite na aba Acesso.'}
        </p>
        {!podeGerir ? (
          <p className="txt-apoio text-tinta-secundaria">Só quem gere a governança desativa ou reativa pessoas. Peça ao administrador do sistema.</p>
        ) : eu ? (
          <p className="txt-apoio text-tinta-secundaria">Você não muda a sua própria situação: outro administrador faz isso.</p>
        ) : pessoa.ativo ? (
          <div>
            <BotaoDestrutivo
              rotulo="Desativar pessoa"
              pergunta={`Desativar ${pessoa.nome}?`}
              consequencia={`${
                abertas.length === 0 ? 'Se houver sessão aberta, ela cai na hora.' : `A sessão cai na hora ${nosAparelhos}.`
              } Os lançamentos de ${primeiroNome} continuam na trilha, e dá para reativar depois.`}
              rotuloConfirmar="Desativar pessoa"
              rotuloCancelar="Manter ativa"
              {...(gravar.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
              pedirMotivo="pessoa.desativar"
              aoConfirmar={(motivo) => {
                gravar.mutate({ id: pessoa.id, campos: { ativo: false, motivo } });
              }}
            />
          </div>
        ) : (
          <div>
            <Botao
              nivel="secundario"
              disabled={gravar.isPending}
              onClick={() => {
                gravar.mutate({ id: pessoa.id, campos: { ativo: true, motivo: 'pessoa reativada pela administração na mesa' } });
              }}
            >
              Reativar pessoa
            </Botao>
          </div>
        )}
        <MensagemDeErro erro={gravar.error} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ alcadas

function VistaDeAlcadas({ termo, pagina, porPagina, aoCadastrar, irPara }: PropsDaLista) {
  const consulta = useAlcadas();
  const remover = useRemoverAlcada();
  const sessao = useSessao();
  const gere = (sessao.data?.permissoes ?? []).includes('governanca.gerir');

  const todas = consulta.data ?? [];
  const linhas = todas.filter((alcada) => combinaComTermo([alcada.papel_nome, NOME_DO_ASSUNTO[alcada.assunto]], termo));
  const fatia = paginar(linhas, pagina, porPagina);

  const colunas: readonly Coluna<Alcada>[] = [
    { chave: 'papel', rotulo: 'Papel', celula: (linha) => linha.papel_nome },
    {
      chave: 'assunto',
      rotulo: 'Aprova',
      celula: (linha) => NOME_DO_ASSUNTO[linha.assunto] ?? linha.assunto,
    },
    {
      chave: 'limite',
      rotulo: 'Limite',
      historia: 'NUM-CAD-04',
      alinhamento: 'numero',
      celula: (linha) =>
        linha.limite_valor === null ? (
          <span className="text-tinta-secundaria">Sem teto</span>
        ) : (
          /* Valor de dinheiro em preto e com `tabular-nums`, pelo utilitario
             `numero` (regras 3 e 4). Sem `Medida`: `formatarReais` ja traz o
             "R$", e uma unidade vazia desenharia um espaco cinza sem sentido. */
          <span className="numero text-tinta">{formatarReais(Number(linha.limite_valor))}</span>
        ),
    },
    {
      chave: 'segundo',
      rotulo: 'Segundo aprovador',
      celula: (linha) => (linha.exige_segundo_aprovador ? 'Exigido' : 'Não exigido'),
    },
  ];

  /* A acao da linha (p.18) pela `acoes` da tabela: a direita, no hover e no
     foco, fora da conta das oito colunas e sem cabecalho escrito — so para o
     leitor de tela. Sempre com confirmacao: tirar a alcada apaga a linha. */
  const acaoDaLinha = (linha: Alcada) => {
    const assunto = (NOME_DO_ASSUNTO[linha.assunto] ?? linha.assunto).toLowerCase();
    return (
      <BotaoDestrutivo
        rotulo="Tirar alçada"
        pergunta={`Tirar a alçada de ${assunto} do papel ${linha.papel_nome}?`}
        consequencia={`A linha sai da lista, e o papel ${linha.papel_nome} deixa de ter alçada de ${assunto}. A retirada fica na trilha, com o seu nome e a data.`}
        rotuloConfirmar="Tirar alçada"
        rotuloCancelar="Manter a alçada"
        {...(remover.isPending ? { motivoIndisponivel: 'Espere o pedido anterior terminar.' } : {})}
        pedirMotivo="alcada.tirar"
        aoConfirmar={(motivo) => {
          remover.mutate({ id: linha.id, motivo });
        }}
      />
    );
  };

  return (
    <>
      <BarraDeBusca
        termo={termo}
        contagem={
          consulta.isPending ? null : `${formatarInteiro(linhas.length)} de ${formatarInteiro(todas.length)} — alçadas`
        }
        placeholder="Papel ou assunto"
        irPara={irPara}
      />

      <MensagemDeErro erro={remover.error} />

      <Tabela
        rotulo="Alçadas de aprovação"
        colunas={colunas}
        linhas={fatia.visiveis}
        chaveDaLinha={(linha) => linha.id}
        {...(gere ? { acoes: acaoDaLinha } : {})}
        carregando={consulta.isPending}
        erro={
          consulta.isError ? (
            <FalhaAoCarregar
              erro={consulta.error}
              oque="as alçadas"
              aoRecarregar={() => {
                void consulta.refetch();
              }}
            />
          ) : null
        }
        vazio={
          todas.length === 0 ? (
            <EstadoVazio
              titulo="Nenhuma alçada definida"
              descricao={
                aoCadastrar
                  ? 'Defina quem aprova compra, ajuste, ordem de serviço e liberação, e até que valor.'
                  : 'Sem alçada, aprova quem tem a permissão de aprovar. Quem define as alçadas é o perfil Administrador.'
              }
              acao={
                aoCadastrar ? (
                  <Botao nivel="secundario" onClick={aoCadastrar}>
                    Cadastrar alçada
                  </Botao>
                ) : undefined
              }
            />
          ) : (
            <SemResultado titulo="Nenhuma alçada com esses filtros" oque="todas as alçadas" irPara={irPara} />
          )
        }
        rodape={<Rodape total={linhas.length} fatia={fatia} porPagina={porPagina} irPara={irPara} />}
      />
    </>
  );
}

function CadastroDeAlcada({
  id,
  gravar,
  aoConcluir,
}: {
  readonly id: string;
  readonly gravar: ReturnType<typeof useGravarAlcada>;
  readonly aoConcluir: () => void;
}) {
  const papeis = usePapeis();
  const [papelId, setPapelId] = useState('');
  const [assunto, setAssunto] = useState<Assunto>('compra');
  const [limite, setLimite] = useState('');
  const [segundo, setSegundo] = useState('nao');
  const obrigatorios = useObrigatorios({ papel: 'Escolha o papel que aprova para poder gravar.' });
  const idDoPapel = obrigatorios.idDe('papel');
  const erroDoPapel = obrigatorios.erroDe('papel');

  return (
    <form
      id={id}
      noValidate
      className="formulario-grade"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!obrigatorios.podeEnviar({ papel: papelId })) return;
        const valor = limite.trim().replace(/\./g, '').replace(',', '.');
        gravar.mutate(
          {
            papelId,
            assunto,
            limiteValor: valor === '' ? null : Number(valor),
            exigeSegundoAprovador: segundo === 'sim',
          },
          { onSuccess: aoConcluir },
        );
      }}
    >
      {/* Campo + lista, e nao `CampoSelecao`: o erro do obrigatorio vem de
          fora, ao sair do campo e na rede do envio. */}
      <Campo id={idDoPapel} rotulo="Papel que aprova" erro={erroDoPapel}>
        {/* As opcoes ainda vem (p.32): so com "Escolha um papel" a lista
            deixava escolher o nada. */}
        {papeis.isPending ? (
          <ControleCarregando oQue="Carregando os papéis" />
        ) : (
          /* O seletor do sistema (p.23, p.31): com mais de sete papeis ele
             abre com busca, e com um so ja vem escolhido. */
          <ListaDeEscolha
            id={idDoPapel}
            rotulo="Papel que aprova"
            valor={papelId}
            aoMudar={(valor) => {
              obrigatorios.limpar('papel');
              setPapelId(valor);
            }}
            aoSair={() => {
              obrigatorios.conferir('papel', papelId);
            }}
            rotuloVazio="Escolha um papel"
            opcoes={(papeis.data ?? []).filter((p) => p.ativo).map((p) => ({ valor: p.id, rotulo: p.nome }))}
            idDoErro={erroDoPapel ? `${idDoPapel}-erro` : undefined}
          />
        )}
      </Campo>
      <CampoSelecao
        rotulo="Assunto"
        valor={assunto}
        opcoes={ASSUNTOS.map((chave) => ({ valor: chave, rotulo: NOME_DO_ASSUNTO[chave] }))}
        aoMudar={(valor) => {
          if ((ASSUNTOS as readonly string[]).includes(valor)) setAssunto(valor as Assunto);
        }}
      />
      <CampoTexto
        rotulo="Limite em reais"
        valor={limite}
        opcional
        ajuda="Em branco quer dizer sem teto. Zero quer dizer que esse papel não aprova nenhum valor."
        validar={(valor) =>
          valor.trim() === '' || /^[\d.]+(,\d{1,2})?$/.test(valor.trim())
            ? null
            : 'Informe o valor com vírgula decimal, como 15.000,00.'
        }
        aoMudar={setLimite}
      />
      <CampoSelecao
        rotulo="Exige segundo aprovador"
        valor={segundo}
        opcoes={[
          { valor: 'nao', rotulo: 'Não' },
          { valor: 'sim', rotulo: 'Sim' },
        ]}
        aoMudar={setSegundo}
      />
      {gravar.error ? (
        <div className="campo-linha">
          <MensagemDeErro erro={gravar.error} />
        </div>
      ) : null}
    </form>
  );
}

// ------------------------------------------------------------ estados da lista

/**
 * Vazio POR FILTRO (p.18 e p.23): a lista tem linhas, a busca e que nao achou
 * nenhuma. Dizer "nenhum papel cadastrado" aqui seria falso — e a saida e
 * limpar o filtro, nao cadastrar.
 */
function SemResultado({
  titulo,
  oque,
  irPara,
}: {
  readonly titulo: string;
  readonly oque: string;
  readonly irPara: (estado: EstadoDaBusca) => void;
}) {
  return (
    <EstadoVazio
      titulo={titulo}
      descricao={`Apague a busca ou limpe os filtros para ver ${oque}.`}
      acao={
        <Botao
          nivel="secundario"
          onClick={() => {
            irPara({ termo: '' });
          }}
        >
          Limpar filtros
        </Botao>
      }
    />
  );
}

/**
 * O estado de erro da lista, com o texto que o servidor mandou e a saida como
 * acao (p.23: nunca estado sem acao).
 *
 * O 401 tem frase propria porque ele nao e defeito desta tela: a sessao caiu,
 * e chamar isso de "falha ao carregar" mandaria quem le procurar problema no
 * lugar errado.
 */
function FalhaAoCarregar({
  erro,
  oque,
  aoRecarregar,
}: {
  readonly erro: unknown;
  readonly oque: string;
  readonly aoRecarregar: () => void;
}) {
  const daApi = erro instanceof ErroDaApi ? erro : null;
  return (
    <EstadoVazio
      titulo={daApi?.status === 401 ? 'Sua sessão não está aberta' : `Não foi possível carregar ${oque}`}
      descricao={
        daApi?.comoResolver ??
        'A lista não chegou. Tente carregar de novo; se continuar, avise o suporte com o horário.'
      }
      acao={
        <Botao nivel="secundario" onClick={aoRecarregar}>
          Carregar a lista
        </Botao>
      }
    />
  );
}
